'use client';

import { useState } from 'react';

import { useWorkspaceRoutes } from '@/hooks/useWorkspaceRoutes';
import { formatDueDateLabel, getTimelineStatusLabel } from '@/utils/tasks';
import { Avatar, DataBadge, Meter, SecondaryButton, SmartInput } from '@web/ui';
import {
  backendErrorsToFieldErrors,
  formatHours,
  getErrorMessage,
  notify,
} from '@web/utils';

import styles from './MyTasksSubRowForm.module.scss';

import type { Task, UpdateTaskPayload } from '@/features/tasks/types';

type MyTasksSubRowFormProps = {
  task: Task;
  onUpdateTask: (args: {
    taskId: string;
    payload: UpdateTaskPayload;
  }) => Promise<unknown>;
};

type FormErrors = Partial<Record<'description' | 'form', string>>;

function mapBackendPath(path: string): string | null {
  if (path === 'description.0' || path === 'description') return 'description';
  if (path === 'non_field_errors.0') return 'form';

  return path;
}

function getPrimaryAssignee(task: Task) {
  return task.assignees?.[0] ?? null;
}

function getAssigneeName(task: Task): string {
  const assignee = getPrimaryAssignee(task)?.user;
  if (!assignee) return 'Unassigned';

  const fullName =
    `${assignee.first_name ?? ''} ${assignee.last_name ?? ''}`.trim();
  return fullName || assignee.email || 'Unknown user';
}

function getAssigneeAvatar(task: Task): string | undefined {
  const assignee = getPrimaryAssignee(task)?.user;
  const rawAssignee = assignee as typeof assignee & {
    avatar_url?: string | null;
    avatar?: string | null;
  };

  return rawAssignee?.avatar_url ?? rawAssignee?.avatar ?? undefined;
}

function getContextLabel(task: Task) {
  if (task.job?.name) {
    return {
      primary: task.job.name,
      secondary: task.deliverable?.name ?? 'Job task',
    };
  }

  const companyName = task.client?.company?.name;
  if (companyName) {
    return {
      primary: 'Quick Task',
      secondary: companyName,
    };
  }

  return {
    primary: 'Quick Task',
    secondary: 'Internal',
  };
}

function formatStatusLabel(status: Task['status']) {
  return status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export function MyTasksSubRowForm({
  task,
  onUpdateTask,
}: MyTasksSubRowFormProps) {
  const workspaceRoutes = useWorkspaceRoutes();
  const primaryAssignee = getPrimaryAssignee(task);
  const context = getContextLabel(task);
  const [description, setDescription] = useState(task.description ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  const clearError = (field: keyof FormErrors) => {
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const saveDescription = async () => {
    const nextDescription = description.trim() || null;
    const currentDescription = task.description?.trim() || null;

    if (nextDescription === currentDescription) {
      return;
    }

    try {
      setIsSaving(true);
      setErrors({});
      await onUpdateTask({
        taskId: task.id,
        payload: { description: nextDescription },
      });
    } catch (error) {
      const mappedErrors = backendErrorsToFieldErrors(
        (error as any)?.data?.errors,
        { mapPath: mapBackendPath },
      );

      if (Object.keys(mappedErrors).length > 0) {
        setErrors(mappedErrors as FormErrors);
      }

      notify({ type: 'error', message: getErrorMessage(error) });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={styles.myTasksSubRowForm}>
      <div className={styles.myTasksSubRowForm__description}>
        <SmartInput
          type="textarea"
          name={`my-task-description-${task.id}`}
          label="Description"
          value={description}
          error={errors.description}
          onChange={(nextValue) => {
            setDescription(nextValue);
            clearError('description');
          }}
          onBlur={() => {
            void saveDescription();
          }}
        />
      </div>

      <div className={styles.myTasksSubRowForm__details}>
        <DataBadge
          className={styles.myTasksSubRowForm__dataBadge}
          title="Status"
          value={formatStatusLabel(task.status)}
        />

        <DataBadge
          className={styles.myTasksSubRowForm__dataBadge}
          title="Assignee"
          value={
            <span className={styles.myTasksSubRowForm__assignee}>
              <Avatar
                size="small"
                name={getAssigneeName(task)}
                src={getAssigneeAvatar(task)}
              />
              {getAssigneeName(task)}
            </span>
          }
        />

        <DataBadge
          className={styles.myTasksSubRowForm__dataBadge}
          title="Context"
          value={context.primary}
          description={context.secondary}
        />

        <DataBadge
          className={styles.myTasksSubRowForm__dataBadge}
          title="Service type"
          value={primaryAssignee?.service_type?.label ?? 'Unassigned'}
        />

        <DataBadge
          className={styles.myTasksSubRowForm__dataBadge}
          title="Due date"
          value={formatDueDateLabel(task.due_date)}
        />

        <DataBadge
          className={styles.myTasksSubRowForm__dataBadge}
          title="LOE / Timeline"
          value={
            <div className={styles.myTasksSubRowForm__meterStack}>
              <Meter
                percentage={
                  primaryAssignee?.actual_time_tracked_percentage ?? 0
                }
                label={`LOE ${formatHours(primaryAssignee?.tracked_hours)} / ${formatHours(primaryAssignee?.level_of_effort)}h`}
                ariaLabel="LOE meter"
              />
              <Meter
                percentage={task.due_date_time_percentage ?? 0}
                label={getTimelineStatusLabel(task.due_date, task.days_to_due)}
                ariaLabel="Timeline meter"
              />
            </div>
          }
        />
      </div>

      <div className={styles.myTasksSubRowForm__footer}>
        <p className={styles.myTasksSubRowForm__status}>
          {errors.form ? errors.form : isSaving ? 'Saving changes...' : ''}
        </p>

        {task.job_id ? (
          <SecondaryButton
            ariaLabel="Open job"
            variant="medium"
            onClick={() => {
              window.location.assign(workspaceRoutes.jobs.detail(task.job_id!));
            }}
          >
            <span>Open Job</span>
          </SecondaryButton>
        ) : null}
      </div>
    </div>
  );
}
